
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Fallback: ensure profile, subscription and categories exist for the user
  const ensureUserProfile = useCallback(async (u: User) => {
    if (!u?.id || !u?.email) return;
    try {
      // Check if profils_utilisateur exists
      const { data: profile } = await supabase
        .from('profils_utilisateur')
        .select('id')
        .eq('id', u.id)
        .maybeSingle();

      if (!profile) {
        console.info('ensureUserProfile: creating missing profile for', u.email);
        // Create profils_utilisateur
        await supabase.from('profils_utilisateur').upsert(
          { id: u.id, email: u.email, is_demo: false },
          { onConflict: 'id' }
        );
      }

      // Check if abonnement exists
      const { data: sub } = await supabase
        .from('abonnements')
        .select('id')
        .eq('user_id', u.id)
        .limit(1)
        .maybeSingle();

      if (!sub) {
        console.info('ensureUserProfile: creating missing subscription for', u.email);
        await supabase.from('abonnements').insert({
          user_id: u.id,
          plan: 'gratuit',
          statut: 'actif',
        });
      }

      // Check if categories exist
      const { count } = await supabase
        .from('categories')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', u.id);

      if (!count || count === 0) {
        console.info('ensureUserProfile: creating default categories for', u.email);
        await supabase.rpc('create_default_categories', { user_id_param: u.id });
      }
    } catch (err) {
      console.error('ensureUserProfile: error', err);
    }
  }, []);

  useEffect(() => {
    // Garder la même référence d'objet user tant que l'id ne change pas.
    // Évite que tous les useEffect([user]) (refetch bateaux, maintenances, geo...)
    // se relancent en boucle à chaque événement auth (TOKEN_REFRESHED, focus, etc.)
    const applyUser = (nextUser: User | null) => {
      setUser((prev) => {
        if (prev?.id && nextUser?.id && prev.id === nextUser.id) {
          return prev; // même utilisateur -> on conserve la référence
        }
        return nextUser ?? null;
      });
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.info(`${new Date().toISOString()} auth:onAuthStateChange`, { event, user: currentSession?.user?.email, id: currentSession?.user?.id });
        setSession(currentSession);
        applyUser(currentSession?.user ?? null);
        setLoading(false);

        // On sign in only, ensure profile exists (deferred to avoid deadlock)
        if (currentSession?.user && event === 'SIGNED_IN') {
          setTimeout(() => ensureUserProfile(currentSession.user), 0);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      console.info(`${new Date().toISOString()} auth:getSession`, { user: currentSession?.user?.email, id: currentSession?.user?.id, hasSession: !!currentSession });
      setSession(currentSession);
      applyUser(currentSession?.user ?? null);
      setLoading(false);

      if (currentSession?.user) {
        setTimeout(() => ensureUserProfile(currentSession.user), 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [ensureUserProfile]);

  const signUp = async (email: string, password: string) => {
    console.info('auth:signUp:start', { email });
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`
      }
    });
    if (error) {
      console.error('auth:signUp:error', { email, error });
    } else {
      // Detect fake signup (user already exists) - identities array is empty
      const isRepeatedSignup = data?.user?.identities?.length === 0;
      if (isRepeatedSignup) {
        console.warn('auth:signUp:repeated', { email });
        return { error: { message: 'already registered' } as any };
      }
      console.info('auth:signUp:ok', { email });
    }
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    console.info('auth:signIn:start', { email });
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      console.error('auth:signIn:error', { email, error });
    } else {
      console.info('auth:signIn:ok', { email });
    }
    return { error };
  };

  const signOut = async () => {
    console.info('auth:signOut:start');
    await supabase.auth.signOut();
    console.info('auth:signOut:ok');
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
